/**
 * Command Router
 * Maps command IDs to handler functions.
 * This is the single place that maps command id -> handler function.
 */

import { COMMAND_REGISTRY } from "./commandRegistry";
import { proxyPing, insertPingResult } from "../commands/ribbonHandlers";
import * as tp from "../taskpane/taskpaneHandlers";

export type CommandHandler = (...args: any[]) => Promise<void> | void;

/**
 * Map of command IDs to their handler functions.
 * Populated by importing handlers from commands.ts and taskpaneHandlers.ts.
 */
export const COMMAND_HANDLERS: Record<string, CommandHandler> = {
  // Ribbon handlers
  proxyPing: proxyPing as CommandHandler,
  insertPingResult: insertPingResult as CommandHandler,
  // Taskpane handlers (using tp_ prefix)
  tp_signIn: tp.signIn,
  tp_signOut: tp.signOut,
  tp_pingProxy: tp.pingProxy,
  tp_refreshPing: tp.refreshPing,
  tp_authTest: tp.authTest,
  tp_insertPingResult: tp.insertPingResult,
  tp_fetchMarketHolidays: tp.fetchMarketHolidays,
  tp_insertMarketIsOpen: tp.insertMarketIsOpen,
  tp_insertMarketNextTradingDay: tp.insertMarketNextTradingDay,
  tp_insertMarketNextSessionOpen: tp.insertMarketNextSessionOpen,
  tp_insertMarketNextSessionClose: tp.insertMarketNextSessionClose,
  tp_insertOptionFromTaskPane: tp.insertOptionFromTaskPane,
  tp_updateOptionPreview: tp.updateOptionPreview,
  tp_addColumns: tp.addColumns,
  tp_wrapFormulas: tp.wrapFormulas,
  tp_refreshSelectedOptions: tp.refreshSelectedOptions,
  tp_refreshSheetOptions: tp.refreshSheetOptions,
  tp_insertChain: tp.insertChain,
};

/**
 * Validates that all registry entries have handlers and no extra handlers exist.
 * @throws Error if handlers don't match registry
 */
export function validateCommandHandlers(): void {
  const registryIds = new Set(COMMAND_REGISTRY.map((entry) => entry.id));
  const handlerIds = new Set(Object.keys(COMMAND_HANDLERS));

  const missing: string[] = [];
  const extra: string[] = [];

  for (const registryId of registryIds) {
    if (!handlerIds.has(registryId)) {
      missing.push(registryId);
    }
  }

  for (const handlerId of handlerIds) {
    if (!registryIds.has(handlerId)) {
      extra.push(handlerId);
    }
  }

  if (missing.length > 0 || extra.length > 0) {
    const errors: string[] = [];
    if (missing.length > 0) {
      errors.push("Missing handlers: " + missing.join(", "));
    }
    if (extra.length > 0) {
      errors.push("Extra handlers not in registry: " + extra.join(", "));
    }
    throw new Error("Handler-registry mismatch: " + errors.join("; "));
  }
}

// Fail-fast in dev only (but only if handlers are populated)
// Note: In Node scripts, handlers may not be populated yet, so we check
if (process.env.NODE_ENV !== "production" && Object.keys(COMMAND_HANDLERS).length > 0) {
  try {
    validateCommandHandlers();
  } catch (error) {
    // In Node context, handlers may not be populated - that's OK for validation script
    // The validation script will call validateCommandHandlers explicitly
  }
}
